import { useMemo } from "react";
import { IconSun, IconMoon } from "@tabler/icons-react";
import type { ShadowTimelineEntry } from "../lib/types";

type SunSegment = {
  startMinutes: number;
  endMinutes: number;
  type: "sun" | "shadow";
};

type SunTimelineProps = {
  timeline: ShadowTimelineEntry[];
  currentMinutes: number;
  sunriseMinutes: number;
  sunsetMinutes: number;
};

function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function buildSegments(timeline: ShadowTimelineEntry[], sunriseMinutes: number, sunsetMinutes: number): SunSegment[] {
  if (timeline.length === 0) {
    return [];
  }

  const segments: SunSegment[] = [];
  let currentType: "sun" | "shadow" = timeline[0]!.inShadow ? "shadow" : "sun";
  let segmentStart = sunriseMinutes;

  for (let i = 1; i < timeline.length; i++) {
    const entry = timeline[i]!;
    const entryType: "sun" | "shadow" = entry.inShadow ? "shadow" : "sun";

    if (entryType !== currentType) {
      segments.push({ startMinutes: segmentStart, endMinutes: entry.minutesOfDay, type: currentType });
      segmentStart = entry.minutesOfDay;
      currentType = entryType;
    }
  }

  segments.push({ startMinutes: segmentStart, endMinutes: sunsetMinutes, type: currentType });
  return segments;
}

export function SunTimeline({ timeline, currentMinutes, sunriseMinutes, sunsetMinutes }: SunTimelineProps) {
  const segments = useMemo(
    () => buildSegments(timeline, sunriseMinutes, sunsetMinutes),
    [timeline, sunriseMinutes, sunsetMinutes],
  );

  const totalRange = sunsetMinutes - sunriseMinutes;
  if (totalRange <= 0 || segments.length === 0) {
    return null;
  }

  const toPercent = (minutes: number) =>
    Math.max(0, Math.min(100, ((minutes - sunriseMinutes) / totalRange) * 100));

  const currentPercent = toPercent(currentMinutes);
  const isCurrentInRange = currentMinutes >= sunriseMinutes && currentMinutes <= sunsetMinutes;

  const transitions = segments
    .slice(1)
    .map(seg => ({ minutes: seg.startMinutes, type: seg.type }));

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-xs font-medium text-slate-500">Sun path</p>

      <div className="relative h-5 w-full overflow-hidden rounded-full bg-slate-200">
        {segments.map((seg, i) => {
          const left = toPercent(seg.startMinutes);
          const width = toPercent(seg.endMinutes) - left;
          return (
            <div
              key={i}
              className={`absolute inset-y-0 ${seg.type === "sun" ? "bg-amber-300" : "bg-slate-300"}`}
              style={{ left: `${left}%`, width: `${width}%` }}
            />
          );
        })}

        {isCurrentInRange ? (
          <div
            className="absolute top-0 h-full w-0.5 bg-slate-800"
            style={{ left: `${currentPercent}%` }}
          >
            <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-slate-800" />
          </div>
        ) : null}
      </div>

      <div className="relative mt-1 h-9">
        <span className="absolute left-0 top-4 text-[10px] text-slate-400">
          <IconMoon size={10} className="mr-0.5 inline" />
          {formatTime(sunriseMinutes)}
        </span>

        {transitions.map((t, i) => {
          const pct = toPercent(t.minutes);
          if (pct < 10 || pct > 90) {
            return null;
          }
          return (
            <span
              key={i}
              className={`absolute -translate-x-1/2 text-[10px] font-medium text-slate-500 ${
                t.type === "sun" ? "top-0" : "top-4"
              }`}
              style={{ left: `${pct}%` }}
            >
              {t.type === "sun" ? (
                <IconSun size={10} className="mr-0.5 inline text-amber-500" />
              ) : (
                <IconMoon size={10} className="mr-0.5 inline text-slate-400" />
              )}
              {formatTime(t.minutes)}
            </span>
          );
        })}

        <span className="absolute right-0 top-4 text-[10px] text-slate-400">
          {formatTime(sunsetMinutes)}
          <IconMoon size={10} className="ml-0.5 inline" />
        </span>
      </div>
    </div>
  );
}
